/* istanbul ignore file */
import amplitude, { AmplitudeClient, Callback, Config, ServerZone } from 'amplitude-js'
import { AMPLITUDE_API_KEY, IS_WEB, APPLICATION_TYPE, AMPLITUDE_SERVER_ZONE } from '@web/common/config'
import Logger from '@web/common/Logger'
import { Router } from 'vue-router'
import snakeCase from 'lodash.snakecase'

export default class Amplitude {
  private static initiated = false
  private static platform?: AnalyticsPlatformType

  static init (): void {
    if (!AMPLITUDE_API_KEY || AMPLITUDE_API_KEY.trim() === '') {
      Logger.info('Amplitude API key is not set, skipping initialization')
      return
    }

    const init = () => {
      const options: Config = {
        platform: APPLICATION_TYPE,
        serverZone: AMPLITUDE_SERVER_ZONE as ServerZone,
        serverZoneBasedApi: true
      }

      try {
        amplitude.getInstance().init(AMPLITUDE_API_KEY, undefined, options)
        Amplitude.initiated = true
        Logger.info('Amplitude initialized successfully')
      } catch (error) {
        Logger.error('Failed to initialize Amplitude:', error)
      }
    }

    init()
  }

  static logException (): void {
    // Logger.info('Amplitude not init')
  }

  static setUserId (id: string): void {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return
    }

    amplitude.getInstance().setUserId(id)
  }

  static logEvent (event: EventAmplitude): Promise<unknown> {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return Promise.resolve()
    }

    return new Promise(resolve => {
      amplitude.getInstance().logEvent(event.type, event.properties, resolve)
    })
  }

  static setVersionName (version: string): void {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return
    }

    amplitude.getInstance().setVersionName(version)
  }

  static pageView (router: Router) {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return
    }

    const params = {}
    for (const name in router.currentRoute.value.params) {
      params[snakeCase(name)] = String(router.currentRoute.value.params[name])
    }
    Amplitude.logEvent({
      type: 'page_view',
      properties: {
        ...params,
        // eslint-disable-next-line camelcase
        page_title: router.currentRoute.value.meta?.title || '',
        // eslint-disable-next-line camelcase
        page_location: window.location.href,
        // eslint-disable-next-line camelcase
        page_path: IS_WEB ? window.location.pathname : router,
        ...(Amplitude.platform ? { platform: Amplitude.platform } : {})
      }
    })
  }

  static setDeviceId (deviceId: string): void {
    amplitude.getInstance().setDeviceId(deviceId)
  }

  static setUserProperties (properties: Record<string, string | number>): void {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return
    }

    amplitude.getInstance().setUserProperties(properties)
  }

  static setPlatform (platform: AnalyticsPlatformType): void {
    if (!Amplitude.initiated) {
      Amplitude.logException()
      return
    }

    Amplitude.platform = platform
    amplitude.getInstance().setUserProperties({ platform: Amplitude.platform })
  }
}
